import { USER_VISIBILITY_PERMISSIONS } from '@siat-fe/core';
import { TableColumn } from '../../table';
import { WidgetType } from './widget.enum';

export enum DashBoardWidgetId {
  prestazioni_in_corso = '69cd38b4b5ae7b7ef18101b4',
}
export interface WidgetList {
  active: boolean;
  visible: boolean;
  widget?: Widget;
  group?: string;
  index?: number;
  id?: string;
}

export interface MyDashboard {
  id?: string;
  dashboardWidget: WidgetList[];
  nome?: string;
}

export interface WidgetHeaderConfig {
  titleKey: string;
  showViewAll?: boolean;
  showCreate?: boolean;
  createLabel?: string;
  showChangeContext?: boolean;
  changeContextLabel?: string;
}

export interface WidgetActionPermissions {
  create?: string;
  update?: string;
  delete?: string;
  read?: string;
}

/**
 * Derives the permission string for a widget action based on widget ID.
 * Pattern: btn_[action]_[widgetId]
 *
 * @param widgetId - The widget identifier (e.g., 'pic', 'note', 'segnalazioni')
 * @param action - The action type ('create', 'update', 'delete', 'read')
 * @returns Permission string (e.g., 'btn_create_pic')
 *
 * @example
 * getWidgetActionPermission('pic', 'create') // → 'btn_create_pic'
 * getWidgetActionPermission('note', 'update') // → 'btn_update_note'
 */
export function getWidgetActionPermission(
  widgetId: string,
  action: 'create' | 'update' | 'delete' | 'read',
): string {
  return `btn_${action}_${widgetId}`;
}

/**
 * The union of every widget-type-specific `data` payload shape.
 */
export type WidgetData =
  | ListWidgetData
  | TableWidgetData
  | ChartWidgetData
  | CountWidgetData
  | ChipsWidgetData
  | CountListWidgetData[]
  | CountGridWidgetData[]
  | string[]
  | Record<string, unknown>
  | Record<string, unknown>[];

export interface Widget {
  id: string;
  type: WidgetType;
  title: string;
  icon?: string;
  position: WidgetPosition;
  size: WidgetSize;
  config?: WidgetConfig;
  data?: WidgetData;
  isActive: boolean;
  isVisible: boolean;
  lastUpdated?: Date;
  levelAllergy?: string;
  permission?: string;
  headerConfig?: WidgetHeaderConfig;
  actionPermissions?: WidgetActionPermissions;
}

export interface WidgetPosition {
  row: number;
  col: number;
  order?: number;
}

export interface WidgetSize {
  width: number;
  height: number;
  minWidth?: number;
  minHeight?: number;
}

export interface WidgetApiConfig {
  endpoint: string;
  method?: string;
  refreshInterval?: number;
  params?: { [key: string]: string | number | boolean };
  headers?: { [key: string]: string };
  body?: Record<string, unknown>;
  detail?: string;
  sort?: string;
}

export interface WidgetConfig {
  alertMessage?: string;
  [key: string]: unknown;
  columns?: TableColumn[];
  actions?: unknown[];
  fields?: string[]; // For list and table widgets
  labels?: string[]; // Labels for fields
  api?: WidgetApiConfig;
  path?: Path; // For local data source
  filter?: Filter;
  chipKey?: string; // Key used for status chip in card widgets (e.g., 'stato', 'provenienza')
  stackedChips?: boolean; // Card widgets: show the chips on a row below the name instead of beside it
  nameCodeField?: string; // Card widgets: field appended in brackets to the name (e.g., 'codicePatologia')
}

export interface Filter {
  name: string;
  keyword: string;
}

export interface Path {
  viewAll?: string; // Path to view all items
  detail?: string; // Path to item detail, e.g., '/items/'
  delete?: string; // Path to delete endpoint, e.g., 'segnalazione-bozza/delete'
  download?: string;
  changeContext?: string; // Endpoint that returns a URL to redirect the user to
  changeContextParams?: Record<string, string>; // Query params for the changeContext call
  permission?: USER_VISIBILITY_PERMISSIONS;
  keyParams?: string;
  keyParam?: string;
}

// List Widget Data Types
export interface WidgetListItem {
  id: string;
  label: string;
  value?: string;
  icon?: string;
  path?: string;
}

export interface ListWidgetData {
  items: WidgetListItem[];
  totalCount?: number;
  hasMore?: boolean;
}

// Table Widget Data Types
export interface WidgetTableColumn {
  key: string;
  title: string;
  type?: 'text' | 'number' | 'date' | 'badge' | 'action';
  sortable?: boolean;
  width?: string;
}

export interface TableWidgetData {
  columns: WidgetTableColumn[];
  rows: Record<string, unknown>[];
  totalCount?: number;
  currentPage?: number;
  pageSize?: number;
}

// Chart Widget Data Types
export interface ChartDataPoint {
  label: string;
  value: number;
  color?: string;
}

export interface CountListWidgetData {
  nomeSegnalazione?: string;
  count: number;
  stato?: string;
}

export interface ChartWidgetData {
  type: 'line' | 'bar' | 'pie' | 'doughnut';
  data: ChartDataPoint[];
  options?: {
    showLegend?: boolean;
    showGrid?: boolean;
    colors?: string[];
  };
}

// Count Widget Data Types
export interface CountWidgetData {
  value: number;
  label?: string;
  unit?: string;
  previousValue?: number;
  trend?: 'up' | 'down' | 'stable';
  data: Widget;
}

// Diary Widget Data Types
export interface ChipsWidgetData {
  label?: string;
  categorie: string[];
}

// Specific Widget Interfaces
export interface ListWidget extends Omit<Widget, 'data'> {
  type: WidgetType.LIST;
  data?: ListWidgetData;
}

export interface TableWidget extends Omit<Widget, 'data'> {
  type: WidgetType.TABLE;
  data?: TableWidgetData;
}

export interface ChartWidget extends Omit<Widget, 'data'> {
  type: WidgetType.CHART;
  data?: ChartWidgetData;
}

export interface CountWidget extends Omit<Widget, 'data'> {
  type: WidgetType.COUNT;
  data?: CountWidgetData;
}
export interface ChipsWidget extends Omit<Widget, 'data'> {
  type: WidgetType.CHIPS;
  data?: ChipsWidgetData;
}

export interface CountListWidget extends Omit<Widget, 'data'> {
  type: WidgetType.COUNT_LIST;
  data?: CountListWidgetData[];
}

export interface CountGridWidgetData {
  tag: string;
  numSegnalazioni: number;
  numSegnalazioniRitardo: number;
  ritardoMediano: number;
  percentualeInRitardo: number;
  idSegnalazioni?: number[];
  maxGiorniTag: number;
}

export interface CountGridWidget extends Omit<Widget, 'data'> {
  type: WidgetType.COUNT_GRID;
  data?: CountGridWidgetData[];
}

export interface CardWidget extends Omit<Widget, 'data'> {
  type: WidgetType.CARD;
  data?: TableWidgetData;
}

export interface InfoDetailItem {
  label: string;
  field: string;
  format?: 'boolean' | 'date' | 'text';
  showIf?: string;
}

export interface InfoDetailSection {
  title: string;
  items: InfoDetailItem[];
}

export interface InfoDetailWidget extends Omit<Widget, 'data' | 'config'> {
  type: WidgetType.INFO_DETAIL;
  data?: Record<string, unknown>;
  config: WidgetConfig & {
    hasConsensiSIAT: boolean;
    sections: InfoDetailSection[];
    api?: WidgetApiConfig;
  };
}
